// =============================================================================
// bookingWrites — the one way a project booking is created or moved · 3 Nov 2026
// =============================================================================
// James, 3 Nov 2026: "happy to clean that up, as long as I can still create
// bookings and schedule in the project, as that sometimes gets done in quotes
// and other areas." So every doorway stays — the project schedule page, the
// calendar drawer, the build program, a PO, Dispatch — but they all come
// through here, and every row leaves a booking_history trace. Before this,
// three of the five doorways wrote the row and nothing else: no history, and
// the drawer's quick-add had no end time and landed "confirmed" without anyone
// confirming it. The database side is unchanged (project_bookings, mig 325
// columns, the stand-down guard, the two RPCs).
// =============================================================================
import { supabase as supabaseTyped } from "@/lib/supabase";
const supabase = supabaseTyped as any;

export interface NewBooking {
  project_id: string;
  booking_type: string;
  title: string;
  /** a day — the booking runs 07:00 → 17:00 local on it (or to `end_day`) */
  day: string;
  end_day?: string | null;
  description?: string | null;
  notes?: string | null;
  supplier_id?: string | null;
  crq_number?: string | null;
  show_on_schedule?: boolean;
  status?: "tentative" | "confirmed";
}

/** the internal users.user_id for history rows (booking_history.changed_by_user_id references public.users, not auth) */
export async function currentInternalUserId(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    const { data } = await supabase.from("users").select("user_id").eq("auth_user_id", user.id).maybeSingle();
    return data?.user_id ?? null;
  } catch { return null; }
}

export const bookingStartIso = (day: string) => new Date(`${day}T07:00:00`).toISOString();
export const bookingEndIso = (day: string) => new Date(`${day}T17:00:00`).toISOString();

/** insert the booking and its 'created' history row; returns the booking_id. Throws with the database's own words (stand-down guard, RLS). */
export async function createProjectBooking(b: NewBooking, reason: string, userId?: string | null): Promise<string> {
  const uid = userId === undefined ? await currentInternalUserId() : userId;
  const row: Record<string, unknown> = {
    project_id: b.project_id,
    booking_type: b.booking_type,
    title: b.title.trim(),
    description: b.description ?? null,
    scheduled_start: bookingStartIso(b.day),
    scheduled_end: bookingEndIso(b.end_day || b.day),
    status: b.status ?? "tentative",
    notes: b.notes ?? null,
    supplier_id: b.supplier_id ?? null,
    show_on_schedule: b.show_on_schedule ?? b.booking_type === "outage",
    created_by_user_id: uid,
  };
  if (b.booking_type === "outage") {
    if (!b.crq_number?.trim()) throw new Error("An outage needs its CRQ number.");
    row.crq_number = b.crq_number.trim();
  }
  const { data, error } = await supabase.from("project_bookings").insert(row).select("booking_id").single();
  if (error) throw new Error(error.message);
  const { error: e2 } = await supabase.from("booking_history").insert({ booking_id: data.booking_id, change_type: "created", changed_by_user_id: uid, new_value: row, reason });
  if (e2) console.warn("booking created, history row refused:", e2.message);
  return data.booking_id as string;
}

/** move a booking's dates through the RPC (history row, optional 'delayed') */
export async function rescheduleProjectBooking(booking_id: string, startIso: string, endIso: string | null, reason: string, setDelayed = false): Promise<void> {
  const { error } = await supabase.schema("app").rpc("reschedule_project_booking", { p_booking_id: booking_id, p_new_start: startIso, p_new_end: endIso, p_reason: reason, p_set_status_delayed: setDelayed });
  if (error) throw new Error(error.message);
}

/** tentative → confirmed → in_progress → completed, or cancelled — through the RPC so actual_start / actual_end and history are kept */
export async function setProjectBookingStatus(booking_id: string, status: string, reason: string): Promise<void> {
  const { error } = await supabase.schema("app").rpc("set_project_booking_status", { p_booking_id: booking_id, p_new_status: status, p_reason: reason });
  if (error) throw new Error(error.message);
}
